import { useRef, useEffect } from "react";
import { Repeat, ChevronUp, ChevronDown, Mic } from "lucide-react";
import { DemoBadge } from "@/components/Badges";

export default function LyricsPanel({ segments = [], currentTime, onSeek, isDemo }) {
  const containerRef = useRef(null);
  const activeIdx = segments.findIndex((s) => currentTime >= s.start && currentTime < s.end);
  const idx = activeIdx === -1 ? (currentTime >= (segments[segments.length - 1]?.end || 0) ? segments.length - 1 : -1) : activeIdx;

  useEffect(() => {
    if (idx < 0 || !containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-line="${idx}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [idx]);

  const replay = () => idx >= 0 && onSeek(segments[idx].start);
  const prev = () => idx > 0 ? onSeek(segments[idx - 1].start) : idx === -1 && segments[0] && onSeek(segments[0].start);
  const next = () => idx >= 0 && idx < segments.length - 1 && onSeek(segments[idx + 1].start);

  if (!segments.length) {
    return (
      <div className="rounded-xl border border-[#222A38] bg-[#131822] p-8 text-center text-sm text-slate-500">
        <Mic className="mx-auto mb-2 text-slate-600" size={22} />
        Lyrics transcription was not run for this song.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-base font-bold text-white">Lyrics</h3>
          {isDemo && <DemoBadge label="DEMO LYRICS" />}
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="lyrics-prev" onClick={prev} className="rounded-lg bg-[#1A202C] p-2 text-slate-300 hover:text-white"><ChevronUp size={16} /></button>
          <button data-testid="lyrics-replay" onClick={replay} className="rounded-lg bg-[#1A202C] p-2 text-cyan-400 hover:text-cyan-300"><Repeat size={16} /></button>
          <button data-testid="lyrics-next" onClick={next} className="rounded-lg bg-[#1A202C] p-2 text-slate-300 hover:text-white"><ChevronDown size={16} /></button>
        </div>
      </div>
      <div ref={containerRef} data-testid="lyrics-scroll" className="max-h-72 space-y-1 overflow-y-auto pr-1">
        {segments.map((s, i) => (
          <button
            key={`${s.start}-${i}`}
            data-line={i}
            data-testid={`lyric-line-${i}`}
            onClick={() => onSeek(s.start)}
            className={`block w-full rounded-lg px-3 py-2 text-left transition-all ${
              i === idx
                ? "bg-cyan-500/10 text-lg font-semibold text-cyan-200"
                : "text-slate-400 hover:bg-white/5"
            }`}
          >
            {s.text}
          </button>
        ))}
      </div>
    </div>
  );
}
